/* eslint-disable no-param-reassign */
import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  ICreateOrViewOfferModalState,
  IServiceRequestMessage,
  IServiceRequestMessageRoom,
  IServiceRequestModalState,
  IServiceRequestsFilters,
  IServiceRequestsTagsFilters,
  IShareFeedbackModalState,
} from '../types/tqConnectTypes/service-requests';

export interface IServiceRequestChatRoomState {
  roomId: string;
  messages: IServiceRequestMessage[];
  allMessagesLoaded: boolean;
  unreadCount: number;
  newMessagesCount?: number;
  loading: boolean;
  hasMore: {
    previous: boolean;
    next: boolean;
  };
}

const initialServiceRequestModalState: IServiceRequestModalState = {
  isOpen: false,
  autoCloseOnSuccess: false,
  serviceRequestId: '',
};

const initialServiceRequestsFilters: IServiceRequestsFilters = {};

const initialCreateOrViewOfferModalState: ICreateOrViewOfferModalState = {
  isOpen: false,
  mode: 'create',
  serviceRequestId: '',
  canAcceptOrReject: false,
  respondedBy: null,
};

const initialShareFeedbackModalState: IShareFeedbackModalState = {
  isOpen: false,
  serviceRequestId: '',
  serviceRequestResponseId: '',
  respondedBy: null,
};

interface ITQConnectServiceRequestSliceState {
  isServiceRequestSocketConnected: boolean;
  serviceRequestModalState: IServiceRequestModalState;
  serviceRequestsFilters: IServiceRequestsFilters;
  serviceRequestsTagsFilters: IServiceRequestsTagsFilters;
  createOrViewOfferModalState: ICreateOrViewOfferModalState;
  shareFeedbackModalState: IShareFeedbackModalState;
  selectedServiceRequestMessageRoom: IServiceRequestMessageRoom | null;
  serviceRequestChatRoom: IServiceRequestChatRoomState;
  showScrollToBottom: boolean;
}

const initialServiceRequestsTagsFilters: IServiceRequestsTagsFilters = {
  watchTags: [],
  ignoreTags: [],
};

const initialServiceRequestChatRoomState: IServiceRequestChatRoomState = {
  messages: [],
  roomId: '',
  allMessagesLoaded: false,
  unreadCount: 0,
  loading: false,
  hasMore: {
    previous: true,
    next: false,
  },
};

const initialState: ITQConnectServiceRequestSliceState = {
  isServiceRequestSocketConnected: false,
  serviceRequestModalState: initialServiceRequestModalState,
  serviceRequestsFilters: initialServiceRequestsFilters,
  serviceRequestsTagsFilters: initialServiceRequestsTagsFilters,
  createOrViewOfferModalState: initialCreateOrViewOfferModalState,
  shareFeedbackModalState: initialShareFeedbackModalState,
  selectedServiceRequestMessageRoom: null,
  serviceRequestChatRoom: initialServiceRequestChatRoomState,
  showScrollToBottom: false,
};

const tqConnectServiceRequestSlice = createSlice({
  name: 'tqConnectServiceRequest',
  initialState,
  reducers: {
    setIsServiceRequestSocketConnected: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isServiceRequestSocketConnected = action.payload;
    },
    setServiceRequestModalState: (
      state,
      action: PayloadAction<Partial<IServiceRequestModalState>>,
    ) => {
      Object.assign(state.serviceRequestModalState, action.payload);
    },
    setServiceRequestsFilters: (
      state,
      action: PayloadAction<Partial<IServiceRequestsFilters>>,
    ) => {
      Object.assign(state.serviceRequestsFilters, action.payload);
    },
    setServiceRequestsTagsFilters: (
      state,
      action: PayloadAction<Partial<IServiceRequestsTagsFilters>>,
    ) => {
      Object.assign(state.serviceRequestsTagsFilters, action.payload);
    },
    setCreateOrViewOfferModalState: (
      state,
      action: PayloadAction<Partial<ICreateOrViewOfferModalState>>,
    ) => {
      Object.assign(state.createOrViewOfferModalState, action.payload);
    },
    setShareFeedbackModalState: (
      state,
      action: PayloadAction<Partial<IShareFeedbackModalState>>,
    ) => {
      Object.assign(state.shareFeedbackModalState, action.payload);
    },
    setSelectedServiceRequestMessageRoom: (
      state,
      action: PayloadAction<IServiceRequestMessageRoom | null>,
    ) => {
      if (action.payload) {
        const newSelectedMessageRoom = { ...action.payload };

        state.selectedServiceRequestMessageRoom = newSelectedMessageRoom;

        // set new chat room state
        state.serviceRequestChatRoom = {
          ...initialServiceRequestChatRoomState,
          roomId: newSelectedMessageRoom._id,
        };
        state.showScrollToBottom = false;
      }
    },
    updateSelectedServiceRequestMessageRoom: (
      state,
      action: PayloadAction<Partial<IServiceRequestMessageRoom>>,
    ) => {
      if (action.payload && state.selectedServiceRequestMessageRoom) {
        Object.assign(state.selectedServiceRequestMessageRoom, action.payload);
      }
    },
    clearSelectedServiceRequestMessageRoom: (state) => {
      state.selectedServiceRequestMessageRoom = null;
      state.serviceRequestChatRoom = initialServiceRequestChatRoomState;
    },
    updateServiceRequestChatRoom: (
      state,
      action: PayloadAction<Partial<IServiceRequestChatRoomState>>,
    ) => {
      if (action.payload) {
        Object.assign(state.serviceRequestChatRoom, action.payload);
      }
    },
    clearServiceRequestChatRoom: (state) => {
      state.serviceRequestChatRoom = initialServiceRequestChatRoomState;
    },
    setShowScrollToBottom: (state, action: PayloadAction<boolean>) => {
      state.showScrollToBottom = action.payload;
    },
    resetServiceRequestModalState: (state) => {
      state.serviceRequestModalState = initialServiceRequestModalState;
    },
    resetServiceRequestsFilters: (
      state,
      action: PayloadAction<Partial<IServiceRequestsFilters>>,
    ) => {
      state.serviceRequestsFilters = {
        ...initialServiceRequestsFilters,
        ...action.payload,
      };
    },
    resetServiceRequestsTagsFilters: (state) => {
      state.serviceRequestsTagsFilters = initialServiceRequestsTagsFilters;
    },
    resetCreateOrViewOfferModalState: (state) => {
      state.createOrViewOfferModalState = initialCreateOrViewOfferModalState;
    },
    resetShareFeedbackModalState: (state) => {
      state.shareFeedbackModalState = initialShareFeedbackModalState;
    },
    resetTQConnectServiceRequestState: () => initialState,
  },
});

export const {
  setIsServiceRequestSocketConnected,
  setServiceRequestModalState,
  setServiceRequestsFilters,
  setServiceRequestsTagsFilters,
  setCreateOrViewOfferModalState,
  setShareFeedbackModalState,
  setSelectedServiceRequestMessageRoom,
  updateSelectedServiceRequestMessageRoom,
  clearSelectedServiceRequestMessageRoom,
  updateServiceRequestChatRoom,
  clearServiceRequestChatRoom,
  setShowScrollToBottom,
  resetServiceRequestModalState,
  resetServiceRequestsFilters,
  resetServiceRequestsTagsFilters,
  resetTQConnectServiceRequestState,
  resetShareFeedbackModalState,
  resetCreateOrViewOfferModalState,
} = tqConnectServiceRequestSlice.actions;

export default tqConnectServiceRequestSlice.reducer;
